import React, { useState } from 'react';
import { Image } from 'react-native';
import type { AthleteProps, LeagueProps, MyPlayerProps, OrderProps, PublicPlayerProps, TeamProps } from '../types';
import { Icons } from '../Components';
import { SocialComponentApi, SocialOrderHelpers } from './api';
import moment from 'moment-mini';
import { AthleteImage } from '../Components/Jerseys';
import { View, Button, Text } from '../Components/Themed';
import { useColors } from '../constants/useColors';
import { MyOrdersHelpers } from '../MarketComponents/components/MyOrderList/api';

type SocialOrderCardProps = {
    player?:PublicPlayerProps | MyPlayerProps,
    show_player?:boolean,
    contest_title:string,
    contest_time:string,
    show_amounts?:boolean,
    order:OrderProps,
    athlete?:AthleteProps,
    maxWidth?:number,
    hide_contest?:boolean,
    team?:TeamProps,
    league?:LeagueProps,
    onTagOrder?:(order:OrderProps) => void,
    onCopyOrder?:(order:OrderProps) => void,
    onFadeOrder?:(order:OrderProps) => void
}
const SocialOrderCard = ({ player, show_player, show_amounts, hide_contest, maxWidth, league, team, athlete, order, contest_title, contest_time, onTagOrder, onCopyOrder, onFadeOrder }:SocialOrderCardProps) => {
    const Colors = useColors();
    const [ fade_loading, setFadeLoading ] = useState(false);
    const cl = order.market_type == 'FOR_MONEY' ? '$' : 'E'
    const { result_label, color } = SocialOrderHelpers.getResultDetails(order)
    const stats = MyOrdersHelpers.calcAllOrderStats([order])
    const handleFadeOrder = async() => {
        if(!onFadeOrder){ return }
        if(fade_loading){ return }
        setFadeLoading(true);
        let reversed_order = await SocialComponentApi.reverseOrder(order);
        if(!reversed_order){ return alert('Unable to fade this order at this time') }
        onFadeOrder({
            ...reversed_order, 
            buy_sell_ind:'buy',
            influencer_id: order.player_id,
            influence_type: 'fade', 
            selling_position_id:undefined, 
            open_amt:10,
            player_id:'', 
            order_type: 'limit',
            collar_pct: 0,
            h2h_id:undefined,
            order_id: '', 
            called_amt: 0, 
            potential_winnings: SocialOrderHelpers.calcPotentialWinnings(reversed_order.odds, 10) 
        })
        setFadeLoading(false);
    }
    return (
        <Button disabled={!onTagOrder} float style={{ minWidth:275, maxWidth, padding:0, flex:1 }} onPress={() => onTagOrder ? onTagOrder(order) : console.log('')}>
            {show_player && player ?
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                <Image
                    source={{ uri: player.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                    style={{ height:35, width:35, borderRadius:100 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text theme='h1'>{player.first_name} {player.last_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>@{player.username}</Text>
                </View>
                <Text size={14} color={color} weight='bold'>{result_label}</Text>
            </View>
            :<></>}
            {!hide_contest ?
            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                <View transparent style={{ flex:1, marginRight:10 }}>
                    <Text theme='description' style={{ flex:1 }}>{contest_title}</Text>
                    <Text style={{ marginTop:3 }} size={12} theme='light' weight='regular'>{contest_time}</Text>
                </View>
                {league ?
                <Image
                    source={{ uri: league.league_image }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode='cover'
                />
                :
                <View transparent style={{ height:30, width:30, borderRadius:4, justifyContent:'center', alignItems:'center', backgroundColor:SocialOrderHelpers.getIconColor(order) }}>
                    <Icons.ActivityIcon size={14} color={Colors.text.white}/>
                </View>
                }
            </View>
            :<></>}
            <View type='body' transparent style={{ padding:10 }}>
                <View transparent type='row' style={{ padding:5 }}>
                    {team ?
                    <Image
                        source={{ uri: team.image?.url }}
                        style={{ height:35, width:35 }}
                        resizeMode='cover'
                    />
                    :athlete ?
                    <AthleteImage
                        athlete={athlete}
                        size={32}
                    />
                    :<></>}
                    <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                        <Text theme='h2'>{order.title}</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Created on {moment(order.create_datetime).format('MM/DD hh:mm a')}</Text>
                    </View>
                    <View transparent>
                        <Text theme='h1'>{SocialOrderHelpers.getOddsLabel(order.odds)}</Text>
                        {!show_player ?
                        <Text style={{ marginTop:3 }} size={14} color={color} theme='h1'>{result_label}</Text>
                        :<></>}
                    </View>
                </View>
            </View>
            {show_amounts && order.resolution_status == 'inprogress'?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h2' textAlign='left'>{cl}{stats.unresolved_stake.toFixed(2)}</Text>
                </View>
                <Text theme='description'>To Win</Text>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1' textAlign='right'>{cl}{stats.unresolved_potential_winnings.toFixed(2)}</Text>
                </View>
            </View>
            :show_amounts && order.resolution_status == 'closed' ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1' textAlign='right' color={stats.net_earnings > 0 ? Colors.text.success : Colors.text.error}>Earned: {cl}{stats.net_earnings.toFixed(2)}</Text>
                </View>
            </View>
            :<></>}
            {order.resolution_status == 'inprogress' ?
            <View type='row' style={{ padding:0, borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                {onCopyOrder ? 
                <Button
                    style={{ flex:1, borderRadius:0, borderBottomLeftRadius:8 }}
                    title='Copy'
                    title_color={Colors.text.white}
                    backgroundColor={Colors.absolutes.brand.midnight}
                    onPress={() => onCopyOrder({ 
                        ...order, 
                        buy_sell_ind:'buy',
                        influencer_id: order.player_id,
                        influence_type: 'copy', 
                        order_type:'limit',
                        collar_pct:0,
                        selling_position_id:undefined, 
                        open_amt:10,
                        player_id:'', 
                        h2h_id:undefined,
                        order_id: '', 
                        called_amt: 0, 
                        potential_winnings: SocialOrderHelpers.calcPotentialWinnings(order.odds, 10) 
                    })}
                />
                :<></>}
                {onFadeOrder ?
                <Button
                    style={{ flex:1, borderRadius:0, borderBottomRightRadius:8, opacity:fade_loading?0.5:1 }}
                    title='Fade'
                    loading={fade_loading}
                    title_color={Colors.text.white}
                    backgroundColor={Colors.absolutes.brand.cyan}
                    onPress={() => handleFadeOrder()}
                />
                :<></>}
            </View>
            :<></>}
        </Button>
    )
}

export default SocialOrderCard